import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { format } from 'date-fns';
import { useNotification } from '../../contexts/NotificationContext';
import { useOTBookingForm } from './shared/hooks/useOTBookingForm';
import { useOTBooking } from './shared/services/useOTBookingService';
import OTBookingForm from './shared/OTBookingForm';
import FormPageLayout from '../../components/FormPageLayout';

const OTBookingPage = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  const { isEdit, loading, existingData, filteredPatients, nextIpNo, submit } = useOTBooking(uhid_no);
  const { formData, errors, setInitialData, handleChange, handleAllergyChange, handlePatientChange, validate, buildPayload } = useOTBookingForm();

  useEffect(() => {
    if (existingData) { setInitialData(existingData); return; }
    if (nextIpNo) setInitialData(prev => ({ ...prev, ip_no: nextIpNo, booking_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm") }));
  }, [existingData, nextIpNo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload = buildPayload();
      payload.surgery_datetime = format(new Date(payload.surgery_datetime), 'yyyy-MM-dd HH:mm:ss');
      if (payload.actual_start_time) payload.actual_start_time = format(new Date(payload.actual_start_time), 'yyyy-MM-dd HH:mm:ss');
      if (payload.actual_end_time) payload.actual_end_time = format(new Date(payload.actual_end_time), 'yyyy-MM-dd HH:mm:ss');
      await submit(payload);
      showNotification('success', isEdit ? `OT Booking for ${formData.patient_name} updated!` : 'OT Booking submitted successfully!');
      
      const returnPage = location.state?.page || 0;
      const returnMenu = location.state?.activeMenu || 'OT Booking';
      
      setTimeout(() => {
        navigate(`/home?page=${returnPage}`, { 
          state: { activeMenu: returnMenu } 
        });
      }, 1000);
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Error saving OT Booking!');
    }
  };

  return (
    <FormPageLayout 
      title={isEdit ? 'Edit OT Booking' : 'OT Booking Slip'} 
      backTo={{ 
        path: `/home?page=${location.state?.page || 0}`, 
        state: { activeMenu: location.state?.activeMenu || 'OT Booking' } 
      }} 
      loading={loading}
    >
      <form onSubmit={handleSubmit}>
        <OTBookingForm formData={formData} errors={errors} handleChange={handleChange} handleAllergyChange={handleAllergyChange} handlePatientChange={handlePatientChange} patients={filteredPatients} isEdit={isEdit} />
        <Button type="submit" variant="contained" fullWidth sx={{ mt: 3, bgcolor: '#008080', '&:hover': { bgcolor: '#006666' }, py: 1.5 }}>
          {isEdit ? 'Update OT Booking' : 'Submit OT Booking'}
        </Button>
      </form>
    </FormPageLayout>
  );
};

export default OTBookingPage;
